/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';

import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { ElectroTooltip } from 'components/ExtendedUiElements/customized';
import ManageAccount from 'components/ManageAccount/ManageAccount';
import { getCountForTables } from 'utils/index';
import * as statuses from 'utils/statuses';
import { createNewAccount, canBatchImport } from 'permissions/account';
import AccountCard from 'views/Accounts/components/AcountCard/AccountCard';
import NavigationService from 'services/NavigationService';

import createReportView from '../components/ReportView/ReportViewBase';
import type { DataViewColumn } from '../components/ReportView/types';
import { actionsListSingle, actionsListMultiple } from './actions';

import styles from './AccountsReportView.module.scss';

const navigationULR = {
  ORDERS: 'orders/',
  MAINASSETS: '/assets',
  ASSETS: 'assets/',
  PROJECTS: '/projects',
  COSTCENTERS: '/cost-centers',
};

const columns: DataViewColumn[] = [
  {
    columnName: 'name',
    title: 'general:trading_accounts.labels.name',
    flexBasis: 240,
    component: function NameCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;

      return (
        <ElectroTooltip title={rowData.name}>
          <Link to={`/accounts/${rowData.id}`} className={disabled ? styles.asDisabled : ''}>
            <span>{display}</span>
          </Link>
        </ElectroTooltip>
      );
    },
  },
  {
    columnName: 'account_no',
    title: 'general:trading_accounts.labels.number',
    flexBasis: 240,
    component: function AccountNumberCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;

      return (
        <Link to={`/accounts/${rowData.id}`} className={disabled ? styles.asDisabled : ''}>
          <span>{display}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'open_contracts_count',
    title: 'general:contract_open_plural',
    flexBasis: 120,
    component: function ContractsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.ORDERS, {
            page: 1,
            filters: [
              {
                key: 'er_status',
                values: ['open'],
              },
              {
                key: 'account',
                values: [rowData.id],
              },
            ],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'closed_contracts_count',
    title: 'general:contract_closed_plural',
    flexBasis: 120,
    component: function ContractsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.ORDERS, {
            page: 1,
            filters: [
              {
                key: 'er_status',
                values: ['closed'],
                overrideImplicit: true,
              },
              {
                key: 'account',
                values: [rowData.id],
              },
            ],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'rental_assets_count',
    title: 'general:asset_rented_plural',
    flexBasis: 120,
    component: function AssetsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.MAINASSETS, {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.id] },
              { key: 'asset_type', values: ['Rented Open'] },
            ],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'sales_assets_count',
    title: 'general:asset_sale_plural',
    flexBasis: 120,
    component: function AssetsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.ASSETS, {
            page: 1,
            filters: [
              {
                key: 'asset_status',
                values: ['Sold'],
              },
              {
                key: 'account',
                values: [rowData.id],
              },
            ],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'owned_assets_count',
    title: 'general:asset_owned_plural',
    flexBasis: 120,
    component: function AssetsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.ASSETS, {
            page: 1,
            filters: [
              { key: 'account', values: [rowData.id] },
              { key: 'asset_type', values: ['Owned'] },
            ],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'projects_count',
    title: 'general:project_plural',
    flexBasis: 120,
    component: function ProjectsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.PROJECTS, {
            page: 1,
            filters: [{ key: 'account', values: [rowData.id] }],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'cost_centres_count',
    title: 'general:cost_center_plural',
    flexBasis: 120,
    component: function CostCentersCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={NavigationService.formulateReportViewUri(navigationULR.COSTCENTERS, {
            page: 1,
            filters: [{ key: 'account', values: [rowData.id] }],
          })}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'locations_count',
    title: 'general:location_plural',
    flexBasis: 120,
    component: function LocationsCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={{
            pathname: `/accounts/${rowData.id}`,
            state: { tab: 2 },
          }}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'users_count',
    title: 'general:user_plural',
    flexBasis: 120,
    component: function UsersCountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;
      const count = getCountForTables(display);

      if (count === 0) {
        return <span className={disabled ? styles.asDisabled : ''}>{count}</span>;
      }

      return (
        <Link
          to={{
            pathname: `/accounts/${rowData.id}`,
            state: { tab: 1 },
          }}
          className={disabled ? styles.asDisabled : ''}
        >
          <span>{count}</span>
        </Link>
      );
    },
  },
  {
    columnName: 'status',
    title: 'general:labels.status',
    flexBasis: 120,
    disableSort: true,
    component: function StatusCell({ display }) {
      return <StatusIndicator status={display} />;
    },
  },
  {
    columnName: 'id',
    title: '',
    flexBasis: 200,
    disableSort: true,
    component: function ManageTradingAccountCell({ display, rowData }) {
      const disabled = !rowData.is_enabled;

      return <ManageAccount disabled={disabled} to={`/accounts/${display}`} />;
    },
  },
];

export default createReportView({
  codename: 'accounts',
  title: 'general:trading_account_plural',
  type: 'TABBED',
  userFilterNamespace: 'Accounts',
  supportedLayouts: ['GRID', 'TABLE'],
  defaultLayout: 'GRID',
  disableDownload: true,
  views: {
    enabled: {
      title: statuses.getI18nCode('active').code,
      dataPathname: 'accounts?only_enabled=true',
      apiType: 'SQL',
      filtersPathnameOverride: 'accounts/filters',
      gridItemComponent: AccountCard,
      actions: {
        single: actionsListSingle,
        multi: actionsListMultiple,
      },
      columns,
      idKey: 'id',
    },
    disabled: {
      title: statuses.getI18nCode('disabled').code,
      dataPathname: 'accounts?only_disabled=true',
      apiType: 'SQL',
      filtersPathnameOverride: 'accounts/filters',
      gridItemComponent: AccountCard,
      actions: {
        single: actionsListSingle,
        multi: actionsListMultiple,
      },
      columns,
      idKey: 'id',
    },
    archived: {
      title: statuses.getI18nCode('archived').code,
      dataPathname: 'accounts?only_archived=true',
      apiType: 'SQL',
      filtersPathnameOverride: 'accounts/filters',
      gridItemComponent: AccountCard,
      actions: {
        single: actionsListSingle,
        multi: actionsListMultiple,
      },
      columns,
      idKey: 'id',
    },
  },
  buttons: [
    {
      label: 'general:actions.new',
      icon: '#icon-add',
      subActions: [
        {
          label: 'general:actions.create_new_trading_account',
          to: '/accounts/create',
          permissions: createNewAccount,
        },
        {
          label: 'general:actions.import_brands',
          to: '/brands/create-multiple',
          permissions: canBatchImport,
        },
      ],
    },
  ],
});
